import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal, Button, TextInput, NumberInput, Select, Loader } from '@mantine/core'
import { useAxiosApi } from '@/hooks/useAxiosApi'
import { useDebouncedValue } from '@mantine/hooks'
import { IconCheck, IconBuilding } from '@tabler/icons-react'

export default function RetailerCreate({ opened, onClose }) {
    const navigate = useNavigate()
    const { requestGet, requestPost } = useAxiosApi()

    const [search, setSearch] = useState('')
    const [debouncedSearch] = useDebouncedValue(search, 400)
    const [searchResults, setSearchResults] = useState([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [selectedCompany, setSelectedCompany] = useState(null)

    const [markupRate, setMarkupRate] = useState(40)
    const [hasFreeShipping, setHasFreeShipping] = useState('paid')
    const [isCreating, setIsCreating] = useState(false)

    useEffect(() => {
        if (debouncedSearch.length < 2) {
            setSearchResults([])
            return
        }

        let cancelled = false
        setSearchLoading(true)

        requestGet(`/retailers/sellsy-search?q=${encodeURIComponent(debouncedSearch)}`)
            .then(({ data }) => { if (!cancelled) setSearchResults(data) })
            .catch(() => {})
            .finally(() => { if (!cancelled) setSearchLoading(false) })

        return () => { cancelled = true }
    }, [debouncedSearch])

    const reset = () => {
        setSearch('')
        setSearchResults([])
        setSelectedCompany(null)
        setMarkupRate(40)
        setHasFreeShipping('paid')
    }

    const handleClose = () => {
        reset()
        onClose()
    }

    const create = async () => {
        if (!selectedCompany || isCreating) return
        setIsCreating(true)
        try {
            const { data } = await requestPost('/retailers', {
                sellsy_company_id: selectedCompany.id,
                default_markup_rate: markupRate / 100,
                has_free_shipping: hasFreeShipping === 'free',
            })
            handleClose()
            navigate(`/retailers/${data.id}`)
        } catch {
            setIsCreating(false)
        }
    }

    return (
        <Modal opened={opened} onClose={handleClose} title="Nouveau revendeur" size="md">
            <div className="flex flex-col gap-4">
                {!selectedCompany ? (
                    <>
                        <TextInput
                            label="Rechercher un client Sellsy"
                            placeholder="Nom de la société..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            rightSection={searchLoading ? <Loader size="xs" /> : null}
                            autoFocus
                        />

                        {searchResults.length > 0 && (
                            <div className="flex flex-col border divide-y rounded-sm">
                                {searchResults.map((company) => (
                                    <button
                                        key={company.id}
                                        disabled={!!company.already_linked}
                                        className={`flex items-center gap-3 px-4 py-3 text-left transition-colors ${company.already_linked ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-gray-50'}`}
                                        onClick={() => setSelectedCompany(company)}
                                    >
                                        <IconBuilding size={16} className="text-gray-400 shrink-0" />
                                        <div className="flex flex-col min-w-0">
                                            <span className="font-medium text-sm truncate">{company.name}</span>
                                            {company.already_linked && (
                                                <span className="text-xs text-gray-500">Déjà associé à {company.linked_retailer_name}</span>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}

                        {debouncedSearch.length >= 2 && !searchLoading && searchResults.length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-2">Aucun résultat</p>
                        )}
                    </>
                ) : (
                    <div className="flex items-center gap-3 p-3 border rounded-sm">
                        <IconBuilding size={16} className="text-gray-400 shrink-0" />
                        <span className="font-medium text-sm flex-1">{selectedCompany.name}</span>
                        <button className="text-xs text-gray-500 hover:text-gray-700" onClick={() => { setSelectedCompany(null) }}>
                            Changer
                        </button>
                    </div>
                )}

                {selectedCompany && (
                    <>
                        <NumberInput
                            label="Taux de marque"
                            value={markupRate}
                            onChange={setMarkupRate}
                            min={0}
                            max={100}
                            step={1}
                            allowDecimal={false}
                            clampBehavior="strict"
                            suffix="%"
                        />

                        <Select
                            label="Frais de port"
                            value={hasFreeShipping}
                            onChange={setHasFreeShipping}
                            data={[
                                { value: 'paid', label: 'Payants' },
                                { value: 'free', label: 'Offerts' },
                            ]}
                        />

                        <Button onClick={create} loading={isCreating} className="mt-1">
                            Créer le revendeur
                        </Button>
                    </>
                )}
            </div>
        </Modal>
    )
}
